<script setup lang="ts">
    import { computed, onMounted, onBeforeUnmount, watch, nextTick, ref, shallowReactive, type Ref } from 'vue'
    import { useRoute, onBeforeRouteLeave } from 'vue-router'

    import { numCompare, SavingState, toPercentageString, toStringSmartly } from '@common/Common'
    import PortfolioNavigation from '@common/Components/PortfolioNavigation.vue'
    import PortfolioScenarioNavigation from '@Quest/PortfolioScenarios/Components/PortfolioScenarioNavigation.vue'
    import ProjectsSelectionView from "@Quest/PortfolioScenarios/Components/ProjectsSelectionView.vue"

    import { ProjectAcceptance } from '@common/types'
    import { KendoChart, KendoChartDataSerie, KendoChartOptions } from '@Kendo/Components/KendoChart'

    import { ProjectAcceptanceDTO } from "@Quest/PortfolioScenarios/dtos"
    import { useSelectionPageQueryV1} from '@Quest/PortfolioScenarios/queries-vue-composables'
    import { useUpdateScenarioAcceptancesCommandV1 } from '@Quest/PortfolioScenarios/commands-vue-composables'

    import type {
        SelectionCostConstraintReadModel,
        SelectionCriterionReadModel,
        SelectionPageReadModel,
        SelectionProjectReadModel,
        SelectionResourceConstraintReadModel
    } from '@Quest/PortfolioScenarios/readmodels'

    import { UpdateScenarioAcceptancesCommandV1 } from '../commands-core'

    const route = useRoute()
    const workspaceId = computed(() => Number(route.params?.workspaceId ?? 0))
    const tchProjectId = computed(() => Number(route.params?.tchProjectId ?? 0))
    const scenarioId = computed(() => String(route.params?.scenarioId ?? ''))

    const {
        runSelectionPageQueryV1,
        modelSelectionPageQueryV1
    } = useSelectionPageQueryV1(workspaceId, tchProjectId, scenarioId)

    const rm = modelSelectionPageQueryV1 as Ref<SelectionPageReadModel | null>
    const portfolioId = computed(() => rm.value?.PortfolioId)


    // ======================
    // Command
    // ======================

    const {
        invokeUpdateScenarioAcceptancesCommandV1,
        onSucceedUpdateScenarioAcceptancesCommandV1,
        command: updateScenarioAcceptancesCommandV1
    } = useUpdateScenarioAcceptancesCommandV1(workspaceId, portfolioId, scenarioId)


    // ======================
    // Local State
    // ======================

    const projects = ref<SelectionProjectReadModel[] | null>(null)

    const searchPhrase = ref<string | null>(null)
    const projectsFilter = ref<string>('All')

    const criteriaSectionExpanded = ref(false)
    const constraintsSectionExpanded = ref(false)
    const resourcesSectionExpanded = ref(false)

    const savingState = ref<SavingState>(SavingState.Saved)

    let autoSaveHandle: NodeJS.Timeout | null = null


    // ======================
    // Computed
    // ======================

    const filteredProjects = computed(() =>
    {
        let view = new Array(...(projects.value ?? []))

        if (searchPhrase.value)
        {
            const phrase = searchPhrase.value.toLowerCase()
            view = view.filter(a => a.Name.toLowerCase().indexOf(phrase) > -1)
        }

        switch (projectsFilter.value)
        {
            case 'Accepted':
                return view.filter(a => a.UiAcceptance === ProjectAcceptance.Accepted)
            case 'Rejected':
                return view.filter(a => a.UiAcceptance === ProjectAcceptance.Rejected)
            case 'TBD':
                return view.filter(a => a.UiAcceptance === ProjectAcceptance.TBD)
            default:
                return view
        }
    })

    const topCriteria = computed<SelectionCriterionReadModel[]>(() =>
        rm.value?.Criteria.filter(a => a.IsTop === true) ?? []
    )


    // ======================
    // Formatters
    // ======================

    function fmtNum(val: number | null)
    {
        return toStringSmartly(val)
    }

    function fmtPct(val: number | null)
    {
        return toPercentageString(val)
    }


    // ======================
    // Constraint styling
    // ======================

    function getResourceCardClass(r: SelectionResourceConstraintReadModel)
    {
        if (r.Limit == null) return ''
        const ratio = r.Allocation / r.Limit
        if (ratio > 1) return 'over-limit'
        if (ratio > 0.85) return 'near-limit'
        return ''
    }

    function getCostCardClass(r: SelectionCostConstraintReadModel)
    {
        if (r.Limit == null) return ''
        const ratio = r.Allocation / r.Limit
        if (ratio > 1) return 'over-limit'
        if (ratio > 0.85) return 'near-limit'
        return ''
    }

    function handleAcceptSelection(p: SelectionProjectReadModel)
    {
        p.UiAcceptance =
            p.UiAcceptance === ProjectAcceptance.Accepted
                ? ProjectAcceptance.TBD
                : ProjectAcceptance.Accepted

        savingState.value = SavingState.Pending
    }

    function handleRejectSelection(p: SelectionProjectReadModel)
    {
        p.UiAcceptance =
            p.UiAcceptance === ProjectAcceptance.Rejected
                ? ProjectAcceptance.TBD
                : ProjectAcceptance.Rejected

        savingState.value = SavingState.Pending
    }

    function handleLockSelection(p: SelectionProjectReadModel)
    {
        p.UiAcceptanceLocked = true
        savingState.value = SavingState.Pending
    }

    function handleUnlockSelection(p: SelectionProjectReadModel)
    {
        p.UiAcceptanceLocked = false
        savingState.value = SavingState.Pending
    }

    function handleUpdateScenarioAcceptancesCommand()
    {
        if (savingState.value !== SavingState.Pending || !projects.value) return

        const acceptances: ProjectAcceptanceDTO[] = projects.value
            .filter(a =>
                a.UiAcceptance !== a.DbAcceptance ||
                a.UiAcceptanceLocked !== a.DbAcceptanceLocked
            )
            .map(a =>
            {
                const x = new ProjectAcceptanceDTO()
                x.Acceptance = a.UiAcceptance
                x.Id = a.Id
                x.Lock = a.UiAcceptanceLocked
                return x
            })

        if (!acceptances.length) return

        invokeUpdateScenarioAcceptancesCommandV1({ Acceptances: acceptances })
        savingState.value = SavingState.InProgress
    }

    function onSelected(result: any)
    {
        projects.value = null;
        runSelectionPageQueryV1();
    }

    function onScenarioUpdated()
    {
        projects.value = null;
        runSelectionPageQueryV1();
    }

    onMounted(() =>
    {
        document.title = "Portfolio Plan - Selection"
        runSelectionPageQueryV1()

        autoSaveHandle = setInterval(
            () => handleUpdateScenarioAcceptancesCommand(),
            10000
        )
    })

    watch(rm, async (model) =>
    {
        if (!model) return

        if (projects.value == null)
        {
            projects.value = model.Projects
        }

        EfficientFrontier.SetData(projects.value, model.CurrencyLabel)
        PrioritizationMatrix.SetData(projects.value, model.CurrencyLabel)

        await nextTick()

        EfficientFrontier.Render()
        PrioritizationMatrix.Render()
    })

    onSucceedUpdateScenarioAcceptancesCommandV1(() =>
    {
        savingState.value = SavingState.Saved

        const cmd = updateScenarioAcceptancesCommandV1.value as UpdateScenarioAcceptancesCommandV1

        for (const acc of cmd.Acceptances)
        {
            const project = projects.value.find(p => p.Id === acc.Id)

            if (project)
            {
                project.DbAcceptance = acc.Acceptance as ProjectAcceptance
                project.DbAcceptanceLocked = acc.Lock
            }
        }

        runSelectionPageQueryV1()
    })


    onBeforeUnmount(() =>
    {
        if (autoSaveHandle) clearInterval(autoSaveHandle)
    })

    onBeforeRouteLeave(() =>
    {
        handleUpdateScenarioAcceptancesCommand()
    })


    class EfficientFrontierChart
    {
        isRendered: boolean = false;

        Chart: KendoChart;

        constructor()
        {
            const options = new KendoChartOptions();
            options.HideLegend();
            options.SetPannable(true);
            options.SetZoomable(true);
            options.SetTooltip(false, '#= dataItem.Name #');
            //options.SetOnSeriesHover(this.page.Highlight);
            //options.SetOnSeriesClick(this.page.OnSeriesClick);

            const chart = KendoChart.CreateScatterChart(options);
            this.Chart = chart;
        }

        SetData(projects: SelectionProjectReadModel[], currencyLabel: string)
        {

            let dataItems: { AlternativeId: string, Name: string, Acceptance: ProjectAcceptance, Color: string | null, X: number, Y: number, Z: number, TooltipHtml: string }[] = [];

            for (const alternativeVM of projects)
            {
                const xValue = alternativeVM.TotalCost;
                const yValue = alternativeVM.TotalValue;
                const zValue = alternativeVM.CostOfValuePoint;

                dataItems.push({
                    Name: alternativeVM.Name,
                    X: xValue as number,
                    Y: yValue as number,
                    Z: zValue as number,
                    AlternativeId: alternativeVM.Id,
                    Acceptance: alternativeVM.DbAcceptance,
                    Color: null,
                    TooltipHtml: `${alternativeVM.Name}`
                });
            }

            dataItems = dataItems.filter(a => a.X != null && a.Y != null && a.Z != null).sort((a, b) => numCompare(a.Z, b.Z));

            for (let i = 1; i < dataItems.length; i++) //from 1 to skip first
            {
                dataItems[i].X += dataItems[i - 1].X;
                dataItems[i].Y += dataItems[i - 1].Y;
            }

            const accepted = new KendoChartDataSerie();
            accepted.SetXField("X");
            accepted.SetYField("Y");
            accepted.SetCategoryField("Name");
            //serie.SetColorField("Color");
            accepted.SetColor("#28a745");
            accepted.SetMarkers(true, "circle"); // circle, square, triangle, cross
            accepted.SetData(dataItems.filter(a => a.Acceptance === ProjectAcceptance.Accepted));

            const rejected = new KendoChartDataSerie();
            rejected.SetXField("X");
            rejected.SetYField("Y");
            rejected.SetCategoryField("Name");
            //serie.SetColorField("Color");
            rejected.SetColor("#dc3545");
            rejected.SetMarkers(true, "cross"); // circle, square, triangle, cross
            rejected.SetData(dataItems.filter(a => a.Acceptance === ProjectAcceptance.Rejected));

            const tbd = new KendoChartDataSerie();
            tbd.SetXField("X");
            tbd.SetYField("Y");
            tbd.SetCategoryField("Name");
            //serie.SetColorField("Color");
            tbd.SetColor("#FFA500");
            tbd.SetMarkers(true, "triangle"); // circle, square, triangle, cross
            tbd.SetData(dataItems.filter(a => a.Acceptance === ProjectAcceptance.TBD));

            const series: KendoChartDataSerie[] = [accepted, rejected, tbd];


            const options = this.Chart.GetOptions();
            options.SetTooltip(true, "#= dataItem.TooltipHtml #");

            const xMaxValue = Math.max(...dataItems.map(v => v.X));
            const yMaxValue = Math.max(...dataItems.map(v => v.Y));

            const xAxisMin = 0;
            const xAxisMax = xMaxValue * 1.1;
            const yAxisMin = 0;
            const yAxisMax = yMaxValue * 1.1;

            const xAxisLabelFormat = xMaxValue > 10 ? "{0:n0}" : null;
            const yAxisLabelFormat = yMaxValue > 10 ? "{0:n0}" : null;

            const xCrosshairTooltipTemplate = xMaxValue ? (e: any): string => kendo.toString(e.value / xMaxValue, "p0") : undefined;
            const yCrosshairTooltipTemplate = yMaxValue ? (e: any): string => kendo.toString(e.value / yMaxValue, "p0") : undefined;

            options.SetXAxis(null, xAxisMin, xAxisMax, `Cost (${currencyLabel})`, xAxisLabelFormat, 2, true, true, null, xCrosshairTooltipTemplate);
            options.SetYAxis(null, yAxisMin, yAxisMax, "Value", yAxisLabelFormat, true, true, null, yCrosshairTooltipTemplate);

            this.Chart.SetOptions(options);
            this.Chart.SetDataSeries(series);

            this.Chart.UpdateOptions();
        }

        Render()
        {
            if (this.isRendered === false)
            {
                this.Chart.Render1("EfficientFrontier");
                this.isRendered = true;
            }
        }
    }

    class PrioritizationMatrixChart
    {
        isRendered: boolean = false;

        Chart: KendoChart;

        constructor()
        {
            const options = new KendoChartOptions();
            options.HideLegend();
            options.SetPannable(true);
            options.SetZoomable(true);
            options.SetTooltip(false, '#= dataItem.Name #');
            //options.SetOnSeriesHover(this.page.Highlight);
            //options.SetOnSeriesClick(this.page.OnSeriesClick);

            const chart = KendoChart.CreateScatterChart(options);
            this.Chart = chart;
        }

        SetData(projects: SelectionProjectReadModel[], currencyLabel: string)
        {
            let dataItems: { AlternativeId: string, Name: string, Acceptance: ProjectAcceptance, Color: string | null, X: number, Y: number, TooltipHtml: string }[] = [];

            for (const alternativeVM of projects)
            {
                const xValue = alternativeVM.TotalCost;
                const yValue = alternativeVM.TotalValue;

                dataItems.push({
                    Name: alternativeVM.Name,
                    X: xValue as number,
                    Y: yValue as number,
                    AlternativeId: alternativeVM.Id,
                    Acceptance: alternativeVM.DbAcceptance,
                    Color: null,
                    TooltipHtml: `${alternativeVM.Name}`
                });
            }

            dataItems = dataItems.filter(a => a.X != null && a.Y != null);

            const accepted = new KendoChartDataSerie();
            accepted.SetXField("X");
            accepted.SetYField("Y");
            accepted.SetCategoryField("Name");
            //serie.SetColorField("Color");
            accepted.SetColor("#28a745");
            accepted.SetMarkers(true, "circle"); // circle, square, triangle, cross
            accepted.SetData(dataItems.filter(a => a.Acceptance === ProjectAcceptance.Accepted));

            const rejected = new KendoChartDataSerie();
            rejected.SetXField("X");
            rejected.SetYField("Y");
            rejected.SetCategoryField("Name");
            //serie.SetColorField("Color");
            rejected.SetColor("#dc3545");
            rejected.SetMarkers(true, "cross"); // circle, square, triangle, cross
            rejected.SetData(dataItems.filter(a => a.Acceptance === ProjectAcceptance.Rejected));

            const tbd = new KendoChartDataSerie();
            tbd.SetXField("X");
            tbd.SetYField("Y");
            tbd.SetCategoryField("Name");
            //serie.SetColorField("Color");
            tbd.SetColor("#FFA500");
            tbd.SetMarkers(true, "triangle"); // circle, square, triangle, cross
            tbd.SetData(dataItems.filter(a => a.Acceptance === ProjectAcceptance.TBD));

            const series: KendoChartDataSerie[] = [accepted, rejected, tbd];

            const options = this.Chart.GetOptions();
            options.SetTooltip(true, "#= dataItem.TooltipHtml #");

            const xMaxValue = Math.max(...dataItems.map(v => v.X));
            const yMaxValue = Math.max(...dataItems.map(v => v.Y));

            const xAxisMin = 0;
            const xAxisMax = xMaxValue * 1.1;
            const yAxisMin = 0;
            const yAxisMax = yMaxValue * 1.1;

            const xAxisLabelFormat = xMaxValue > 10 ? "{0:n0}" : null;
            const yAxisLabelFormat = yMaxValue > 10 ? "{0:n0}" : null;

            options.SetXAxis(null, xAxisMin, xAxisMax, `Cost (${currencyLabel})`, xAxisLabelFormat, 2, true, false, null);
            options.SetYAxis(null, yAxisMin, yAxisMax, "Value", yAxisLabelFormat, true, false, null);

            this.Chart.SetOptions(options);
            this.Chart.SetDataSeries(series);

            this.Chart.UpdateOptions();
        }

        Render()
        {
            if (this.isRendered === false)
            {
                this.Chart.Render1("PrioritizationMatrix");
                this.isRendered = true;
            }
        }
    }

    const EfficientFrontier = shallowReactive(new EfficientFrontierChart())
    const PrioritizationMatrix = shallowReactive(new PrioritizationMatrixChart())
</script>


<template>
    <div>

        <PortfolioNavigation :workspaceId="workspaceId" :projectId="tchProjectId" />

        <div v-if="rm" class="container-fluid" style="margin-top: 80px; padding-top: 20px;">
            <div class="row">
                <div class="col-lg-6">
                    <small class="text-muted">{{rm.PortfolioName}} / {{rm.ScenarioName}}</small>
                    <h1 style="font-size: 2em; font-weight: 300; color: #33475b; margin: 0;">Plan: Selection</h1>
                </div>
                <div class="col-lg-6 d-flex flex-column justify-content-end">
                    <PortfolioScenarioNavigation :portfolioId="rm.PortfolioId"
                                                 :corporateScenarioUrl="rm.CorporateScenarioUrl"
                                                 @selected="onSelected"
                                                 @selector-opened="handleUpdateScenarioAcceptancesCommand"
                                                 @settings-opened="handleUpdateScenarioAcceptancesCommand"
                                                 @scenario-updated="onScenarioUpdated"/>
                </div>
            </div>

            <hr />

            <div class="row mt-5">
                <div class="col-sm-6 col-md-4 col-lg-3 col-xl-3" v-for="m in rm.Metrics" :key="m.Id">
                    <div class="card mt-2">
                        <div class="card-body text-center">
                            <h5 class="card-title">
                                {{m.Title}}
                            </h5>
                            <h6 class="card-subtitle mb-2 text-muted">{{m.Subtitle}}</h6>
                            <span class="h1">{{m.PlannedValueString ?? '&nbsp'}}</span>
                            <br />
                            <span class="h5 text-muted">{{m.PlannedPercentageString ?? '&nbsp' }}</span>

                            <div class="text-left">
                                <hr class="my-3" />
                                <h6>All: {{m.RequestedValueString}} <small class="text-muted" v-if="m.RequestedPercentageString">({{m.RequestedPercentageString}})</small></h6>
                                <h6>Accepted: {{m.AcceptedValueString ?? 'NA'}} <small class="text-muted" v-if="m.AcceptedPercentageString">({{m.AcceptedPercentageString}})</small></h6>
                                <h6>Rejected: {{m.RejectedValueString ?? 'NA'}} <small class="text-muted" v-if="m.RejectedPercentageString">({{m.RejectedPercentageString}})</small></h6>
                                <h6>TBD: {{m.TbdValueString ?? 'NA'}} <small class="text-muted" v-if="m.TbdPercentageString">({{m.TbdPercentageString}})</small></h6>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="card mt-3">
                <div class="card-header pointer" @click="resourcesSectionExpanded = !resourcesSectionExpanded">
                    <h5>Resources</h5>
                </div>
                <div class="card-body" v-if="resourcesSectionExpanded">
                    <div class="row">
                        <div class="col-sm-6 col-md-4 col-lg-3 col-xl-3" v-for="r in rm.ResourceConstraints" :key="r.Id">
                            <div class="card mt-1" :class="getResourceCardClass(r)">
                                <div class="card-body text-center">
                                    <h5 class="card-title">
                                        <!--{{r.Name}} <small>{{r.Unit}}</small>-->
                                        <a class="text-dark" v-sanitizedHref="`/#/portfolio-plan/resource/${workspaceId}/${tchProjectId}/${scenarioId}/${r.Id}`">
                                            {{r.Name}} <small>{{r.Unit}}</small>
                                        </a>
                                    </h5>
                                    <h6 class="card-subtitle mb-2 text-muted">
                                        <span v-if="r.Limit != null">
                                            {{fmtPct(r.Allocation/r.Limit)}}
                                        </span><span v-else>-</span>
                                    </h6>
                                    <span class="h1">
                                        {{fmtNum(r.Allocation)}}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>


            <div class="card mt-3">
                <div class="card-header pointer" @click="constraintsSectionExpanded = !constraintsSectionExpanded">
                    <h5>Costs ({{rm.CurrencyLabel}})</h5>
                </div>
                <div class="card-body" v-if="constraintsSectionExpanded">
                    <div class="row">
                        <div class="col-sm-6 col-md-4 col-lg-3 col-xl-3" v-for="c in rm.CostConstraints" :key="c.Id">
                            <div class="card mt-1" :class="getCostCardClass(c)">
                                <div class="card-body text-center">
                                    <h5 class="card-title">
                                        {{c.Name}}
                                    </h5>
                                    <h6 class="card-subtitle mb-2 text-muted">
                                        <span v-if="c.Limit != null">
                                            {{fmtPct(c.Allocation/c.Limit)}}
                                        </span><span v-else>-</span>
                                    </h6>
                                    <span class="h1">
                                        {{fmtNum(c.Allocation)}}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="card mt-3">
                <div class="card-header pointer" @click="criteriaSectionExpanded = !criteriaSectionExpanded">
                    <h5>Criteria</h5>
                </div>
                <div class="card-body" v-if="criteriaSectionExpanded">
                    <div class="row">
                        <div class="col-sm-6 col-md-4 col-lg-3 col-xl-2" v-for="c in topCriteria" :key="c.Id">
                            <div class="card">
                                <div class="card-body text-center">
                                    <h5 class="card-title">
                                        {{c.Name}}
                                    </h5>
                                    <h6 class="card-subtitle mb-2 text-muted">Value points</h6>
                                    <span class="h1">{{c.TotalWeightedValueString ?? '&nbsp'}}</span>
                                    <span>
                                        <br /><span class="h5 text-muted">{{c.BalanceRatioString}}</span>
                                    </span>
                                    <div class="text-left">
                                        <hr class="my-3" />
                                        <h6>Overall value: {{rm.TotalAcceptedValueString}} <small class="text-muted">(100%)</small></h6>
                                        <h6>Criterion value: {{c.TotalWeightedValueString}} <small class="text-muted">({{c.ValueShareString}})</small></h6>
                                        <h6>Balanced value: {{c.BalancedWeightedValueString}} <small class="text-muted">({{c.GlobalWeightString}})</small></h6>
                                        <h6>Balance ratio: {{c.BalanceRatioString}}</h6>
                                    </div>

                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="row mt-3">
                <div class="col-lg-6 col-md-12">
                    <div class="card">
                        <div class="card-header">
                            <div class="float-right">
                                <div class="dropdown d-inline mr-2">
                                    <button data-toggle="dropdown" class="btn btn-secondary btn-sm">Legend</button>
                                    <div class="dropdown-menu dropdown-menu-right" role="menu">
                                        <span class="dropdown-item-text">
                                            <i class="fa-solid fa-xmark text-danger" aria-hidden="true"></i> Rejected
                                        </span>
                                        <span class="dropdown-item-text">
                                            <i class="fa-solid fa-play fa-rotate-270 text-warning" aria-hidden="true"></i> TBD
                                        </span>
                                        <span class="dropdown-item-text">
                                            <i class="fa-solid fa-circle text-success" aria-hidden="true"></i> Accepted
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <h5>Efficient Frontier</h5>
                        </div>
                        <div class="card-body chart-container">
                            <div id="Chart-EfficientFrontier" style="max-width: 100%; height: 100%; overflow: auto;"></div>
                        </div>
                    </div>
                </div>
                <div class="col-lg-6 col-md-12">
                    <div class="card">
                        <div class="card-header">
                            <div class="float-right">
                                <div class="dropdown d-inline mr-2">
                                    <button data-toggle="dropdown" class="btn btn-secondary btn-sm">Legend</button>
                                    <div class="dropdown-menu dropdown-menu-right" role="menu">
                                        <span class="dropdown-item-text">
                                            <i class="fa-solid fa-xmark text-danger" aria-hidden="true"></i> Rejected
                                        </span>
                                        <span class="dropdown-item-text">
                                            <i class="fa-solid fa-play fa-rotate-270 text-warning" aria-hidden="true"></i> TBD
                                        </span>
                                        <span class="dropdown-item-text">
                                            <i class="fa-solid fa-circle text-success" aria-hidden="true"></i> Accepted
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <h5>Prioritization Matrix</h5>
                        </div>
                        <div class="card-body chart-container">
                            <div id="Chart-PrioritizationMatrix" style="max-width: 100%; height: 100%; overflow: auto;"></div>
                        </div>
                    </div>
                </div>

            </div>

            <br /><br />

            <ProjectsSelectionView :propProjects="filteredProjects"
                                   :propCurrencyLabel="rm.CurrencyLabel"
                                   :propAllCriteria="rm.Criteria"
                                   :propComparableScenarios="rm.ComparableScenarios"
                                   :propSearchPhrase="searchPhrase"
                                   :propProjectsFilter="projectsFilter"
                                   @accept-selection="handleAcceptSelection($event)"
                                   @reject-selection="handleRejectSelection($event)"
                                   @lock-selection="handleLockSelection($event)"
                                   @unlock-selection="handleUnlockSelection($event)"
                                   @update-search-phrase="searchPhrase = $event"
                                   @update-projects-filter="projectsFilter = $event" />

        </div>

        <br />
        <br />
        <br />
        <br />

    </div>

</template>

<style scoped>
    .toolbar {
        background-color: #f8f8f8; /* Light gray background for the toolbar */
        padding: 10px 15px; /* Padding around the toolbar */
        border: 1px solid #e4e4e4; /* A light border to give separation */
        display: flex; /* Use flex to give layout flexibility */
        align-items: center; /* Center-align items vertically */
        justify-content: space-between; /* Separate items */
    }

    .over-limit {
        background-color: #dc3545;
        color: white;
    }

        .over-limit .card-subtitle {
            color: white !important;
        }

    .near-limit {
        background-color: #ffc107;
    }

        .near-limit .card-subtitle {
            color: #212529 !important; 
        }

</style>
